import type { Snapshot } from 'shared';
import { Avatar } from '../design/Avatar';
import { Panel } from '../design/Panel';
import { ChevronBand, Halftone, Label, Paper } from '../design/Paper';
import { Stamp } from '../design/Stamp';
import { ro } from '../i18n/ro';

/**
 * The room's permanent record of who voted how — the app's reason for existing.
 * Projected on the shared screen on the admin's command.
 *
 * Only committed sessions appear: an annulled vote never enters the history at
 * all, rather than being added and retracted.
 */
export function HistoryScreen({ snapshot }: { snapshot: Snapshot }) {
  const entries = [...snapshot.history].reverse();

  return (
    <Paper className="projector flex flex-col" data-testid="history">
      <header className="relative shrink-0 bg-ink px-scr-gut py-scr-2 text-paper">
        <Halftone className="text-paper" />
        <div className="relative flex items-baseline justify-between gap-scr-gut">
          <Label className="text-screen-md">{ro.history}</Label>
          <Label className="text-screen-sm opacity-55">{ro.appName}</Label>
        </div>
      </header>
      <ChevronBand className="shrink-0 text-ink" />

      <main className="min-h-0 flex-1 overflow-hidden px-scr-gut py-scr-2">
        {entries.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <Label className="text-screen-md text-ink-faint">{ro.historyEmpty}</Label>
          </div>
        ) : (
          <div className="flex h-full flex-col gap-scr-1 overflow-hidden">
            {entries.map((entry) => (
              <Panel
                key={entry.id}
                tone="sunk"
                press="sm"
                className="shrink-0 history-entry"
                innerClassName="flex items-center gap-scr-gut px-[1.5cqw] py-scr-1"
              >
                <div className="w-[8cqw] shrink-0">
                  <Label className="text-screen-sm text-ink-soft">
                    {ro.electionNo(entry.index)}
                  </Label>
                </div>

                <div className="w-[24cqw] shrink-0 truncate">
                  <div className="truncate font-display text-screen-sm uppercase">
                    {entry.presidentName}
                  </div>
                  <div className="truncate text-screen-sm text-ink-soft">
                    {entry.chancellorName}
                  </div>
                </div>

                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-[0.6cqw]">
                  {entry.votes.map((vote) => (
                    <div
                      key={vote.playerId}
                      className="flex items-center gap-[0.4cqw] border-2 border-ink px-[0.4cqw] py-[0.2cqh]"
                      style={{
                        background:
                          vote.choice === 'JA' ? 'var(--color-teal)' : 'var(--color-red)',
                        color: 'var(--color-paper)',
                      }}
                    >
                      <Avatar
                        subject={{ ...vote, id: vote.playerId, avatarVersion: 0 }}
                        size="sm"
                        className="h-[4cqh] w-[4cqh] min-h-3 min-w-3 border text-[1.4cqh]"
                      />
                      <span className="max-w-[8cqw] truncate font-display text-screen-sm uppercase">
                        {vote.name}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="shrink-0">
                  <Stamp tone={entry.outcome === 'PASSED' ? 'teal' : 'red'} size="sm">
                    {entry.outcome === 'PASSED' ? ro.governmentApproved : ro.governmentRejected}
                  </Stamp>
                </div>
                <div className="w-[6cqw] shrink-0 text-right font-display text-screen-sm">
                  {entry.jaCount}—{entry.neinCount}
                </div>
              </Panel>
            ))}
          </div>
        )}
      </main>
    </Paper>
  );
}
